/**
 * User + organisation profile — populates the professional PDF
 * report header / signature block, UI language preference, and any
 * future per-user settings.
 *
 * Stored in localStorage rather than on the server: the viewer
 * should continue working on a shared reading-room workstation
 * where each login shares the same device (the logged-in user's
 * OIDC subject keys the localStorage slot). Server-side profiles
 * are a Phase-4-adjacent follow-up — the shape of the DB payload
 * stays identical so the future migration is lift-and-shift.
 */
import { createSignal } from "solid-js";

export type Language = "en" | "vi";

export interface VaultPodProfile {
  /** Displayed on the PDF signature block + header greeting. */
  displayName: string;
  credentials: string;       // "MD", "MD, PhD", "MBBS"
  title: string;             // "Staff Pathologist", "Consultant Histopathologist"
  /** Base64 PNG of a scanned signature. Displayed above the printed
   *  name on the PDF signature block when present. Optional. */
  signatureDataUrl?: string;

  /* Organisation — used to render the PDF letterhead */
  orgName: string;
  orgDepartment: string;
  orgAddress: string;
  orgPhone: string;
  orgEmail: string;
  /** Base64 PNG/SVG of the organisation logo. Renders top-left of
   *  every page of the report when present. */
  orgLogoDataUrl?: string;

  /* UI preferences */
  language: Language;
  /** Prefer the signer's display name / credentials in the report
   *  header even when the rendering user is different — pathology
   *  departments often have a read-only registrar preparing drafts
   *  on behalf of a consultant who later signs. */
  overrideSignerFromProfile: boolean;
}

// Profile is scoped per OIDC subject (the user's `sub` claim).
// Without the per-sub key, two users sharing a workstation would
// see each other's report letterhead, signature, org logo, etc.
// (Live bug surfaced 2026-04-26: admin uploaded an org logo and
// senior saw it on the same browser. Fixed by namespacing the key.)
const KEY_PREFIX = "vpd.profile.v1";

function currentSub(): string {
  try {
    const raw = sessionStorage.getItem("vpd.auth.v1");
    if (!raw) return "anonymous";
    const claims = JSON.parse(raw)?.claims ?? {};
    return String(claims.sub || "anonymous");
  } catch { return "anonymous"; }
}

function key(): string { return `${KEY_PREFIX}:${currentSub()}`; }

const DEFAULT_PROFILE: VaultPodProfile = {
  displayName: "",
  credentials: "",
  title: "",
  orgName: "",
  orgDepartment: "",
  orgAddress: "",
  orgPhone: "",
  orgEmail: "",
  language: "en",
  overrideSignerFromProfile: false,
};

function load(): VaultPodProfile {
  try {
    const raw = localStorage.getItem(key());
    if (!raw) {
      // One-time migration: if the legacy unnamespaced "vpd.profile.v1"
      // key exists and there's no per-sub entry yet, copy it forward
      // so the current user keeps their old profile. The legacy key is
      // deleted afterwards so a different user signing in next can't
      // inherit it.
      const legacy = localStorage.getItem(KEY_PREFIX);
      if (legacy) {
        try { localStorage.setItem(key(), legacy); } catch { /* fine */ }
        try { localStorage.removeItem(KEY_PREFIX); } catch { /* fine */ }
        return { ...DEFAULT_PROFILE, ...JSON.parse(legacy) };
      }
      return DEFAULT_PROFILE;
    }
    return { ...DEFAULT_PROFILE, ...JSON.parse(raw) };
  } catch { return DEFAULT_PROFILE; }
}

function persist(v: VaultPodProfile) {
  try { localStorage.setItem(key(), JSON.stringify(v)); } catch { /* fine */ }
}

const [profile, setProfileInternal] = createSignal<VaultPodProfile>(load());

// Re-load profile when the user changes (e.g. logout + new login on
// the same workstation). Listens for the auth-store signal indirectly
// via a `storage` event on `vpd.auth.v1`.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "vpd.auth.v1" || e.key === null) {
      setProfileInternal(load());
    }
  });
}

export function reloadProfileForCurrentUser(): void {
  setProfileInternal(load());
}

export function updateProfile(patch: Partial<VaultPodProfile>) {
  const next = { ...profile(), ...patch };
  setProfileInternal(next);
  persist(next);
}

export function resetProfile() {
  setProfileInternal(DEFAULT_PROFILE);
  try { localStorage.removeItem(key()); } catch { /* fine */ }
}

export { profile };
